import React from 'react'
import { titleCase } from 'title-case'
import {
  CitationFormatType,
  FullCitationTemplate,
  InTextCitationTemplate,
  SourceType,
  sourceTypeToLabel,
} from '@grammarly/citation-generator'
import { useCitationProgrammaticPageContext } from '../../../../contexts/citation-programmatic-page-context'
import { DefiniteArticleType, IndefiniteArticleType } from '../../../../lib/types/citation-keyword-metadata'
import { CitationProgrammaticPageShortcodeName, CodeName } from './types'
import { Link, Typography } from '@grammarly/badger-ui'

import styles from './citation-programmatic-shortcode.module.scss'
import { useLinkStyleFromTheme } from '../../../ui-lib/link'

interface CitationProgrammaticPageShortcodeProps {
  codeName: CitationProgrammaticPageShortcodeName
}

const { C2 } = Typography

const numOfLinkCols = 3

export const CitationProgrammaticPageShortcode: React.FC<CitationProgrammaticPageShortcodeProps> = (props) => {
  const {
    keywordIndefiniteArticle,
    keywordDefiniteArticle,
    keywordDisplayNameSingular,
    keywordDisplayNamePlural,
    sourceType,
    styleGuide,
    automatedLinks,
  } = useCitationProgrammaticPageContext()

  if (automatedLinks?.length) {
    const numLinksPerColumn = automatedLinks.length / numOfLinkCols
    const remainder = numLinksPerColumn % 1
    const numLinksPerCol1 = remainder ? Math.ceil(numLinksPerColumn) : Math.floor(numLinksPerColumn)
    const numLinksPerCol2 = remainder > 0.5 ? Math.ceil(numLinksPerColumn) : Math.floor(numLinksPerColumn)

    switch (props.codeName) {
      case CodeName.CitationAutomatedLinksCol1: {
        const col1Links = automatedLinks.slice(0, numLinksPerCol1)
        return <LinkList linksList={col1Links} />
      }
      case CodeName.CitationAutomatedLinksCol2: {
        const col2Links = automatedLinks.slice(numLinksPerCol1, numLinksPerCol1 + numLinksPerCol2)
        return <LinkList linksList={col2Links} />
      }
      case CodeName.CitationAutomatedLinksCol3: {
        const col3Links = automatedLinks.slice(numLinksPerCol1 + numLinksPerCol2)
        return <LinkList linksList={col3Links} />
      }
    }
  }
  if (
    keywordIndefiniteArticle &&
    keywordDefiniteArticle &&
    keywordDisplayNameSingular &&
    keywordDisplayNamePlural &&
    sourceType &&
    styleGuide
  ) {
    const keywordSingularIndefinite = getKeywordSingularIndefinite(
      keywordIndefiniteArticle,
      keywordDefiniteArticle,
      keywordDisplayNameSingular
    )

    switch (props.codeName) {
      case CodeName.KeywordSingularIndefinite:
        return <>{keywordSingularIndefinite}</>
      case CodeName.KeywordSingularIndefiniteTitleCased: {
        const keywordSingularIndefiniteTitleCase = getKeywordSingularIndefinite(
          keywordIndefiniteArticle,
          keywordDefiniteArticle,
          titleCase(`${keywordDisplayNameSingular}`)
        )
        return <>{keywordSingularIndefiniteTitleCase}</>
      }
      case CodeName.KeywordSingularIndefiniteSentenceCased:
        return <>{`${keywordSingularIndefinite[0].toUpperCase()}${keywordSingularIndefinite.slice(1)}`}</>
      case CodeName.KeywordSingularDefinite:
        return <>{getKeywordDefinite(keywordDefiniteArticle, keywordDisplayNameSingular)}</>
      case CodeName.KeywordPluralIndefinite:
        return (
          <>{getKeywordPluralIndefinite(keywordIndefiniteArticle, keywordDefiniteArticle, keywordDisplayNamePlural)}</>
        )
      case CodeName.SourceType:
        return <>{getSourceTypeLabel(sourceType)}</>
      case CodeName.SourceTypeSingularIndefinite:
        return <>{getSourceTypeSingularIndefinite(sourceType)}</>
      case CodeName.StyleGuide:
        return <>{styleGuide}</>
      case CodeName.BibliographyPageName:
        return <>{getBibliographyPageName(styleGuide)}</>
      case CodeName.FullCitationTemplate:
        return (
          <div className={styles.citationTemplate}>
            <FullCitationTemplate citationFormat={styleGuide} sourceType={sourceType} />
          </div>
        )
      case CodeName.InTextCitationTemplate:
        return (
          <div className={styles.citationTemplate}>
            <InTextCitationTemplate citationFormat={styleGuide} sourceType={sourceType} />
          </div>
        )
      case CodeName.NarrativeCitationTemplate:
        return (
          <div className={styles.citationTemplate}>&ldquo;The author (Year) provides commentary on&hellip;&rdquo;</div>
        )
    }
  }

  return null
}

function getKeywordSingularIndefinite(
  keywordIndefiniteArticle: IndefiniteArticleType,
  keywordDefiniteArticle: DefiniteArticleType,
  keywordDisplayNameSingular: string
): string {
  switch (keywordIndefiniteArticle) {
    case 'a':
    case 'an':
      return `${keywordIndefiniteArticle} ${keywordDisplayNameSingular}`
    case 'definite article':
      return getKeywordDefinite(keywordDefiniteArticle, keywordDisplayNameSingular)
    case 'none':
      return keywordDisplayNameSingular
  }
}

function getKeywordPluralIndefinite(
  keywordIndefiniteArticle: IndefiniteArticleType,
  keywordDefiniteArticle: DefiniteArticleType,
  keywordDisplayNamePlural: string
): string {
  switch (keywordIndefiniteArticle) {
    case 'definite article':
      return getKeywordDefinite(keywordDefiniteArticle, keywordDisplayNamePlural)
    default:
      return keywordDisplayNamePlural
  }
}

function getKeywordDefinite(keywordDefiniteArticle: DefiniteArticleType, keywordDisplayName: string): string {
  if (keywordDefiniteArticle === 'none') {
    return keywordDisplayName
  }

  return `${keywordDefiniteArticle} ${keywordDisplayName}`
}

function getSourceTypeSingularIndefinite(sourceType: SourceType): string {
  const sourceTypeLowerCase = getSourceTypeLowerCase(sourceType)

  switch (sourceType) {
    case 'Wikipedia':
      return sourceTypeLowerCase
    case 'Image':
    case 'OnlineVideo':
      return `an ${sourceTypeLowerCase}`
    default:
      return `a ${sourceTypeLowerCase}`
  }
}

function getSourceTypeLowerCase(sourceType: SourceType): string {
  const sourceTypeLabel = getSourceTypeLabel(sourceType)

  switch (sourceType) {
    case 'Wikipedia':
      return sourceTypeLabel
    default:
      return `${sourceTypeLabel.toLowerCase()}`
  }
}

function getSourceTypeLabel(sourceType: SourceType): string {
  return sourceTypeToLabel[sourceType]
}

function getBibliographyPageName(styleGuide: CitationFormatType): string {
  switch (styleGuide) {
    case 'APA':
      return 'References'
    case 'MLA':
      return 'Works Cited'
    case 'Chicago':
      return 'Bibliography'
  }
}

function LinkList(props: {
  linksList: {
    url: string
    linkText: string
  }[]
}) {
  const { linksList } = props
  const colorStyles = useLinkStyleFromTheme()
  return (
    <ul className={styles.automatedLinkSection}>
      {linksList.map(({ url, linkText }) => {
        return (
          <C2 as="li" key={linkText} className={styles.automatedLink}>
            <Link href={url} rel="noopener noreferrer" className={colorStyles}>
              {linkText}
            </Link>
            <br />
          </C2>
        )
      })}
    </ul>
  )
}
